<p-toast position="top-center" role="status" aria-live="polite" aria-atomic="true"></p-toast>

<div class="questionnaire-config-container">
  <!-- Breadcrumb -->
  <nav class="mb-4" aria-label="Breadcrumb">
    <p-breadcrumb
      styleClass="custom-breadcrumb"
      [model]="breadcrumbItems()"
      [home]="{ icon: 'pi pi-home', routerLink: '/axa/professionals-dashboard' }"
    >
    </p-breadcrumb>
  </nav>

  <!-- Page Header -->
  <section class="page-header mb-4" aria-labelledby="questionnaire-config-title">
    <div class="header-content">
      <div class="header-left">
        <i class="pi pi-list-check header-icon" aria-hidden="true"></i>
        <div class="header-text">
          <h1 id="questionnaire-config-title">{{ questionnaireId ? 'Modifica Questionario' : 'Crea Questionario' }}</h1>
          <p class="subtitle">Definisci il nome e le domande del questionario</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Main Form Card -->
  <main
    class="card-container surface-card shadow-2 border-round-xl p-4 md:p-5"
    role="main"
  >
    <form [formGroup]="form" class="p-fluid">
    <!-- Sezione Nome Questionario -->
    <div class="field col-12 p-0 mb-5">
      <label for="name" class="block font-bold mb-2 text-900">
        Nome Questionario <span class="required-marker" aria-label="campo obbligatorio">*</span>
      </label>
      <div class="p-inputgroup w-full shadow-sm">
        <input
          pInputText
          id="name"
          formControlName="name"
          placeholder="Inserisci un titolo descrittivo..."
          class="w-full px-3"
          [attr.aria-required]="'true'"
          [attr.aria-invalid]="
            form.get('name')?.invalid && form.get('name')?.touched ? 'true' : 'false'
          "
          [attr.aria-describedby]="
            form.get('name')?.invalid && form.get('name')?.touched ? 'name-error' : null
          "
          [ngClass]="{
            'ng-invalid ng-dirty': form.get('name')?.invalid && form.get('name')?.touched,
          }"
        />
      </div>
      @if (form.get('name')?.invalid && form.get('name')?.touched) {
        <small
          id="name-error"
          class="p-error block mt-2 ml-2 font-semibold"
          role="alert"
          aria-live="assertive"
        >
          <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i> Il nome è obbligatorio
        </small>
      }
    </div>

    <div class="field col-12 p-0 mb-5">
      <label for="questionnaire-tooltip" class="block font-bold mb-2 text-900">
        Tooltip del Questionario
      </label>
      <textarea
        pTextarea
        id="questionnaire-tooltip"
        formControlName="tooltip"
        rows="3"
        class="w-full"
        placeholder="Testo di aiuto mostrato per l'intero questionario (opzionale)"
      ></textarea>
      <small class="text-600 block mt-2 ml-2">
        Compare come informazione contestuale in apertura del questionario.
      </small>
    </div>

    <!-- Sezione Tipo Fiduciario -->
    <div class="field col-12 p-0 mb-5">
      <label for="trusteeTypes" class="block font-bold mb-2 text-900">
        Tipi di Fiduciario <span class="required-marker" aria-label="campo obbligatorio">*</span>
      </label>
      <div class="p-inputgroup w-full shadow-sm">
        <p-multiselect
          id="trusteeTypes"
          [options]="trusteeTypes()"
          formControlName="trusteeTypes"
          optionLabel="name"
          optionValue="code"
          placeholder="Seleziona una o più categorie..."
          display="chip"
          [maxSelectedLabels]="2"
          class="w-full"
          ariaLabel="Tipi di Fiduciario"
          [showClear]="true"
          [filter]="true"
          [attr.aria-required]="'true'"
          [attr.aria-invalid]="
            form.get('trusteeTypes')?.invalid && form.get('trusteeTypes')?.touched ? 'true' : 'false'
          "
          [attr.aria-describedby]="
            form.get('trusteeTypes')?.invalid && form.get('trusteeTypes')?.touched
              ? 'trusteeTypes-error'
              : null
          "
        >
        </p-multiselect>
      </div>
      @if (form.get('trusteeTypes')?.invalid && form.get('trusteeTypes')?.touched) {
        <small
          id="trusteeTypes-error"
          class="p-error block mt-2 ml-2 font-semibold"
          role="alert"
          aria-live="assertive"
        >
          <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i> Seleziona almeno una tipologia fiduciario
          obbligatorio
        </small>
      }
    </div>

    <div class="relative my-6">
      <hr class="border-top-1 border-200" />
    </div>

    <!-- Header Elenco Domande - Mostrato solo se ci sono domande -->
    @if (questions.controls.length > 0) {
      <section
        class="flex align-items-center justify-content-between mb-4 px-2"
        aria-labelledby="fields-list-title"
      >
        <h3 id="fields-list-title" class="font-bold m-0 text-800">
          <i class="pi pi-sort-alt mr-2 text-primary" aria-hidden="true"></i>Elenco Domande
        </h3>
        <p-button
          label="Nuova Domanda"
          icon="pi pi-plus"
          (onClick)="openFieldDialog()"
          [rounded]="true"
          severity="primary"
          size="small"
          ariaLabel="Aggiungi una nuova domanda"
          title="Aggiungi una nuova domanda"
        ></p-button>
      </section>
    } @else {
      <!-- Mostra solo il pulsante quando non ci sono domande -->
      <div class="text-center py-4">
        <p class="text-600 mb-3">
          <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
          Aggiungi almeno una domanda obbligatoria al questionario
        </p>
        <p-button
          label="Nuova Domanda"
          icon="pi pi-plus"
          (onClick)="openFieldDialog()"
          [rounded]="true"
          severity="primary"
          size="large"
          ariaLabel="Aggiungi la prima domanda"
          title="Aggiungi la prima domanda"
        ></p-button>
      </div>
    }

    <!-- Lista Domande (Drag & Drop) -->
    <div
      cdkDropList
      (cdkDropListDropped)="drop($event)"
      class="flex flex-column gap-3 mb-5 min-h-5rem"
      role="list"
      aria-label="Elenco domande del questionario"
    >
      @for (q of questions.controls; let i = $index; track q) {
        <div
          class="field-item p-4 border-1 border-round-xl surface-card shadow-sm transition-all hover:shadow-md border-primary-300"
          cdkDrag
          role="listitem"
          [attr.aria-labelledby]="'question-title-' + i"
        >
          <div
            class="custom-placeholder bg-primary-50 border-dashed border-2 border-primary-200 border-round-xl"
            *cdkDragPlaceholder
          ></div>

          <div class="flex justify-content-between align-items-center mb-3">
            <div class="flex align-items-center gap-2">
              <!-- Pulsanti sposta su/giù: alternativa accessibile al drag-and-drop -->
              <div class="flex flex-column reorder-btns">
                <p-button
                  icon="pi pi-chevron-up"
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  severity="secondary"
                  [ariaLabel]="'Sposta su la domanda: ' + q.value.text"
                  [attr.title]="'Sposta su la domanda: ' + q.value.text"
                  [disabled]="i === 0"
                  (onClick)="moveUp(i)"
                ></p-button>
                <p-button
                  icon="pi pi-chevron-down"
                  [text]="true"
                  [rounded]="true"
                  size="small"
                  severity="secondary"
                  [ariaLabel]="'Sposta giù la domanda: ' + q.value.text"
                  [attr.title]="'Sposta giù la domanda: ' + q.value.text"
                  [disabled]="i === questions.controls.length - 1"
                  (onClick)="moveDown(i)"
                ></p-button>
              </div>
              <!-- Drag handle: trascinamento con indicatore di focus visibile -->
              <div
                class="drag-handle flex flex-column align-items-center text-400 cursor-move"
                cdkDragHandle
                tabindex="0"
                role="button"
                [attr.aria-label]="'Trascina per riordinare: ' + q.value.text"
                [attr.title]="'Trascina per riordinare: ' + q.value.text"
              >
                <i class="pi pi-ellipsis-v" aria-hidden="true"></i>
                <i class="pi pi-ellipsis-v" style="margin-top: -8px" aria-hidden="true"></i>
              </div>
              <div class="flex flex-column gap-1">
                <span class="text-xs font-bold text-primary uppercase"
                  >Posizione {{ q.value.position }}</span
                >
                <strong class="text-800" [attr.id]="'question-title-' + i">{{
                  q.value.text
                }}</strong>
                @if (q.value.tooltip) {
                  <span class="text-600 text-sm font-normal line-height-3">
                    {{ q.value.tooltip }}
                  </span>
                }
              </div>
            </div>

            <div class="flex gap-2">
              <p-button
                icon="pi pi-pencil"
                [text]="true"
                [rounded]="true"
                severity="secondary"
                [ariaLabel]="'Modifica domanda: ' + q.value.text"
                [attr.title]="'Modifica domanda: ' + q.value.text"
                (onClick)="editField(i)"
              ></p-button>
              <p-button
                icon="pi pi-trash"
                [text]="true"
                [rounded]="true"
                severity="danger"
                [ariaLabel]="'Elimina domanda: ' + q.value.text"
                [attr.title]="'Elimina domanda: ' + q.value.text"
                (onClick)="deleteField(i)"
              ></p-button>
            </div>
          </div>

          <div class="flex flex-wrap gap-2 ml-6">
            <p-tag [value]="getQuestionTypeLabel(q.value.type)" severity="secondary" [rounded]="true"></p-tag>
            <p-tag
              [value]="q.value.mandatory ? 'Obbligatorio' : 'Opzionale'"
              [severity]="q.value.mandatory ? 'warn' : 'success'"
              [rounded]="true"
            ></p-tag>
          </div>

          <!-- Anteprima Opzioni -->
          @if (getOptions(i).controls.length > 0) {
            <div class="ml-6 mt-4 p-3 bg-surface-50 border-round-lg border-1 border-100">
              @if (q.value.type === 'BOOLEAN') {
                <div class="py-2 flex align-items-center gap-3">
                  <i
                    class="pi pi-stop text-primary"
                    style="font-size: 0.8rem"
                    aria-hidden="true"
                  ></i>
                  <span class="text-700 font-medium">{{ getOptions(i).at(0).value.text }}</span>
                  @if (getOptions(i).at(0).value.triggersAlert) {
                    <p-tag
                      value="ABILITA ALERT"
                      severity="danger"
                      [rounded]="true"
                      styleClass="text-xs"
                    ></p-tag>
                  }
                </div>
              } @else {
                <ul class="list-none p-0 m-0">
                  @for (opt of getOptions(i).controls; track opt; let last = $last) {
                    <li
                      class="py-2 flex align-items-center gap-3"
                      [ngClass]="{ 'border-bottom-1 border-100': !last }"
                    >
                      <i
                        class="pi pi-circle text-primary"
                        style="font-size: 0.6rem"
                        aria-hidden="true"
                      ></i>
                      <span class="text-700 font-medium">{{ opt.value.text }}</span>
                      @if (opt.value.triggersAlert) {
                        <p-tag
                          value="ALERT"
                          severity="danger"
                          [rounded]="true"
                          styleClass="text-xs"
                        ></p-tag>
                      }
                      @if (opt.value.requiresComment) {
                        <p-tag
                          value="COMMENTO"
                          severity="info"
                          [rounded]="true"
                          styleClass="text-xs"
                        ></p-tag>
                      }
                      @if (opt.value.id === 'SPECIAL_OTHER') {
                        <p-tag
                          value="CAMPO TESTO LIBERO"
                          severity="warn"
                          [rounded]="true"
                          styleClass="text-xs"
                        ></p-tag>
                      }
                    </li>
                  }
                </ul>
              }
            </div>
          }
        </div>
      } @empty {
        <div
          class="text-center py-8 border-2 border-dashed border-200 border-round-xl surface-50"
          role="status"
          aria-live="polite"
        >
          <i class="pi pi-list text-300 text-5xl mb-4" aria-hidden="true"></i>
          <p class="text-secondary m-0">Nessuna domanda presente.</p>
        </div>
      }
    </div>

    <div class="flex justify-content-end gap-3 mt-8 pt-5 border-top-1 border-100"
      role="group"
      [attr.aria-label]="'Azioni form questionario'">
      @if (form.invalid || questions.length === 0) {
        <small class="p-error block text-right mb-2" role="status" aria-live="polite">
          <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
          @if (form.invalid) {
            Compila tutti i campi obbligatori (nome e tipo fiduciario)
          } @else if (questions.length === 0) {
            È obbligatorio aggiungere almeno una domanda al questionario
          }
        </small>
      }
      <p-button
        label="Annulla"
        [outlined]="true"
        (onClick)="navigateToList()"
        [attr.aria-label]="'Annulla e torna al registro questionari'"
        [attr.title]="'Annulla e torna al registro questionari'"
      ></p-button>
      <p-button
        [label]="questionnaireId ? 'Salva Modifiche' : 'Salva'"
        icon="pi pi-check"
        iconPos="right"
        severity="primary"
        (onClick)="submit()"
        [disabled]="form.invalid || questions.length === 0"
        [attr.aria-label]="questionnaireId ? 'Salva le modifiche al questionario' : 'Salva il nuovo questionario'"
        [attr.title]="form.invalid || questions.length === 0 ? (form.invalid ? 'Compila tutti i campi obbligatori per procedere' : 'Aggiungi almeno una domanda per salvare') : (questionnaireId ? 'Salva le modifiche' : 'Salva questionario')"
      ></p-button>
    </div>
  </form>
</main>

<!-- Dialog Laterale -->
<p-dialog
  [(visible)]="showTypeDialog"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  position="right"
  [style]="{ width: '560px', height: '100vh', margin: '0' }"
  (onHide)="resetDialog()"
  ariaLabel="Dialog configurazione domanda"
  aria-labelledby="question-field-dialog-title"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center gap-3">
      @if (fieldForm.type) {
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          [rounded]="true"
          (onClick)="fieldForm.type = ''"
          size="small"
          ariaLabel="Torna alla selezione del tipo domanda"
          title="Torna alla selezione del tipo domanda"
        ></p-button>
      }
      <span id="question-field-dialog-title" class="font-bold">{{
        fieldForm.type ? 'Configura Campo' : 'Scegli Tipo'
      }}</span>
    </div>
  </ng-template>

  <div class="flex flex-column h-full" role="region" aria-labelledby="question-field-dialog-title">
    <div class="flex flex-column gap-3 w-full">
      @if (!fieldForm.type) {
        @for (type of questionTypes; track type.value) {
          <p-button
            [label]="type.label"
            [icon]="type.icon"
            styleClass="w-full justify-content-start p-4 mb-2 shadow-sm"
            severity="secondary"
            [outlined]="true"
            (onClick)="selectType(type.value)"
            [ariaLabel]="'Seleziona tipo domanda: ' + type.label"
            [attr.title]="'Seleziona tipo domanda: ' + type.label"
          ></p-button>
        }
      } @else {
        <div class="flex flex-column gap-5 mt-2">
          <!-- Testo Domanda -->
          <div class="flex flex-column gap-2 p-fluid">
            <label for="question-text-input" class="font-bold">Testo della Domanda</label>
            <input
              pInputText
              id="question-text-input"
              [(ngModel)]="fieldForm.text"
              placeholder="Scrivi la domanda..."
              class="p-3"
              [attr.aria-required]="'true'"
              [attr.aria-invalid]="!fieldForm.text.trim() ? 'true' : 'false'"
              [attr.aria-describedby]="!fieldForm.text.trim() ? 'question-text-error' : null"
            />
            @if (!fieldForm.text.trim()) {
              <small id="question-text-error" class="p-error" role="alert" aria-live="assertive">
                Il testo della domanda è obbligatorio
              </small>
            }
          </div>

          <div class="flex flex-column gap-2 p-fluid">
            <label for="question-tooltip-input" class="font-bold">Tooltip della Domanda</label>
            <textarea
              pTextarea
              id="question-tooltip-input"
              [(ngModel)]="fieldForm.tooltip"
              rows="3"
              placeholder="Testo di aiuto contestuale per questa domanda (opzionale)"
              class="p-3"
            ></textarea>
          </div>

          <!-- Obbligatorio: Mostrato solo per TESTO, NUMERO, DATA e SINGLESELECT -->
          @if (fieldForm.type !== 'MULTISELECT' && fieldForm.type !== 'BOOLEAN') {
            <div
              class="flex align-items-center gap-3 p-3 bg-surface-50 border-round-lg border-1 border-200 shadow-sm"
            >
              <p-checkbox
                [(ngModel)]="fieldForm.mandatory"
                [binary]="true"
                id="mandatoryCheck"
              ></p-checkbox>
              <label for="mandatoryCheck" class="font-semibold cursor-pointer"
                >Campo Obbligatorio</label
              >
            </div>
          }

          <!-- Gestione Opzioni (SINGLE, MULTI, BOOLEAN) -->
          @if (fieldForm.type === 'SINGLESELECT' || fieldForm.type === 'MULTISELECT') {
            <div class="flex flex-column gap-3">
              <div class="flex align-items-center justify-content-between">
                <label class="font-bold">Opzioni</label>
                <p-button
                  label="Aggiungi"
                  icon="pi pi-plus"
                  [text]="true"
                  (onClick)="addOptionInDialog()"
                  ariaLabel="Aggiungi nuova opzione"
                  title="Aggiungi nuova opzione"
                ></p-button>
              </div>

              <!-- Legenda -->
              <div class="flex flex-column gap-1 p-3 surface-100 border-round text-sm text-600">
                <span><i class="pi pi-bell text-red-500 mr-1"></i><strong>Abilita alert</strong>: genera un avviso backoffice quando il professionista sceglie questa risposta.</span>
                <span><i class="pi pi-comment text-blue-500 mr-1"></i><strong>Richiede commento</strong>: quando selezionata, compare un campo testo obbligatorio affiancato alla risposta.</span>
                @if (fieldForm.type === 'SINGLESELECT') {
                  <span><i class="pi pi-pencil text-orange-500 mr-1"></i><strong>Icona matita</strong>: trasforma l'opzione in campo "Altro" (testo libero). Solo un'opzione per domanda.</span>
                }
                @if (fieldForm.type === 'SINGLESELECT' || fieldForm.type === 'MULTISELECT') {
                  <span><i class="pi pi-info-circle mr-1"></i>Puoi abilitare alert e commento su più opzioni contemporaneamente.</span>
                }
              </div>

              <div class="flex flex-column gap-2 overflow-y-auto pr-2" style="max-height: 350px">
                @for (opt of fieldForm.options; let idx = $index; track opt.id) {
                  <div class="flex align-items-center gap-2 p-inputgroup shadow-sm">
                    <span class="p-inputgroup-addon">
                      <i
                        [class]="fieldForm.type === 'SINGLESELECT' ? 'pi pi-circle' : 'pi pi-stop'"
                        [ngClass]="{ 'text-orange-500': opt.id === 'SPECIAL_OTHER' }"
                        aria-hidden="true"
                      ></i>
                    </span>
                    <input
                      pInputText
                      [(ngModel)]="opt.text"
                      placeholder="Testo opzione..."
                      [disabled]="opt.id === 'SPECIAL_OTHER'"
                    />

                    @if (fieldForm.type === 'SINGLESELECT') {
                      <p-button
                        [icon]="opt.id === 'SPECIAL_OTHER' ? 'pi pi-times' : 'pi pi-pencil'"
                        [severity]="opt.id === 'SPECIAL_OTHER' ? 'warn' : 'secondary'"
                        [outlined]="true"
                        (onClick)="toggleAltro(idx)"
                        [pTooltip]="opt.id === 'SPECIAL_OTHER' ? 'Rimuovi opzione Altro (testo libero)' : 'Trasforma in campo Altro (testo libero)'"
                        tooltipPosition="top"
                        [ariaLabel]="opt.id === 'SPECIAL_OTHER' ? 'Rimuovi opzione Altro' : 'Imposta opzione come Altro'"
                        [attr.title]="opt.id === 'SPECIAL_OTHER' ? 'Rimuovi opzione Altro' : 'Trasforma in Altro'"
                      ></p-button>
                    }

                    <div class="flex align-items-center gap-2 px-2 min-w-max">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="opt.triggersAlert"
                        (onChange)="toggleEnableAlert(idx)"
                        [inputId]="'enable-alert-' + idx"
                      ></p-checkbox>
                      <label
                        [for]="'enable-alert-' + idx"
                        class="text-sm font-medium cursor-pointer"
                        >Alert</label
                      >
                    </div>

                    <div class="flex align-items-center gap-2 px-2 min-w-max">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="opt.requiresComment"
                        (onChange)="toggleRequiresComment(idx)"
                        [inputId]="'requires-comment-' + idx"
                        [disabled]="opt.id === 'SPECIAL_OTHER'"
                      ></p-checkbox>
                      <label
                        [for]="'requires-comment-' + idx"
                        class="text-sm font-medium cursor-pointer"
                        >Commento</label
                      >
                    </div>

                    <p-button
                      icon="pi pi-times"
                      severity="danger"
                      [outlined]="true"
                      (onClick)="removeOptionInDialog(idx)"
                      [ariaLabel]="'Rimuovi opzione ' + (idx + 1)"
                      [attr.title]="'Rimuovi opzione ' + (idx + 1)"
                    ></p-button>
                  </div>
                }
              </div>
            </div>
          }

          @if (fieldForm.type === 'BOOLEAN') {
            <div class="flex flex-column gap-3">
              <label for="boolean-option-text" class="font-bold"
                >Testo Opzione Checkbox</label
              >
              <div class="flex align-items-center gap-3">
                <div
                  class="flex align-items-center gap-2 p-inputgroup shadow-sm"
                  style="max-width: 320px"
                >
                  <span class="p-inputgroup-addon">
                    <i class="pi pi-check-square" aria-hidden="true"></i>
                  </span>
                  <input
                    pInputText
                    id="boolean-option-text"
                    [ngModel]="fieldForm.options[0]?.text || ''"
                    (ngModelChange)="updateBooleanOptionText($event)"
                    placeholder="Testo opzione..."
                    [attr.aria-required]="'true'"
                    [attr.aria-invalid]="
                      !(fieldForm.options[0]?.text || '').trim() ? 'true' : 'false'
                    "
                    [attr.aria-describedby]="
                      !(fieldForm.options[0]?.text || '').trim()
                        ? 'boolean-option-text-error'
                        : null
                    "
                  />
                </div>

                @if (!(fieldForm.options[0]?.text || '').trim()) {
                  <small
                    id="boolean-option-text-error"
                    class="p-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    Il testo opzione è obbligatorio
                  </small>
                }

                <div class="flex align-items-center gap-2 px-2 min-w-max">
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="fieldForm.options[0]?.triggersAlert"
                    (onChange)="toggleEnableAlert(0)"
                    inputId="enable-alert-boolean"
                  ></p-checkbox>
                  <label for="enable-alert-boolean" class="text-sm font-medium cursor-pointer"
                    >Abilita alert</label
                  >
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>

    @if (fieldForm.type) {
      <div class="mt-auto pt-4 border-top-1 border-100 p-fluid">
        <p-button
          [label]="isEditing ? 'Aggiorna Campo' : 'Aggiungi Domanda'"
          icon="pi pi-check"
          (onClick)="saveField()"
          [disabled]="!canSaveField()"
          severity="primary"
          [ariaLabel]="isEditing ? 'Aggiorna campo domanda' : 'Aggiungi domanda'"
          [attr.title]="isEditing ? 'Aggiorna campo domanda' : 'Aggiungi domanda'"
        ></p-button>
      </div>
    }
  </div>
</p-dialog>
</div> <!-- Close questionnaire-config-container -->
